import { IncidentRosterPerson, PersonStatus } from '../Types/Incident';
import { AlphaRange, personMatchesAlphaRange, getLastName } from './alphaRangeHelpers';
import { personMatchesPersonType, personMatchesSourceFilter } from './personHelpers';
import type { SourceFilter } from './personHelpers';
import { getUiPersonStatus } from './headcountStatus';
import { applyTenantCompanyFilter } from './tenantCompanyHelpers';

// ── Types ──

export type SortMode = 'lastNameAsc' | 'lastNameDesc' | 'statusPriority' | 'recentlyUpdated' | 'floor' | 'company';

export const SORT_OPTIONS: { value: SortMode; label: string }[] = [
  { value: 'lastNameAsc', label: 'Last Name Ascending' },
  { value: 'lastNameDesc', label: 'Last Name Descending' },
  { value: 'statusPriority', label: 'Status priority' },
  { value: 'recentlyUpdated', label: 'Recently updated' },
  { value: 'floor', label: 'Floor' },
  { value: 'company', label: 'Company' },
];

export interface HeadcountFilterState {
  searchText: string;
  statusFilter: PersonStatus | 'all';
  sortMode: SortMode;
  alphaFilter: AlphaRange;
  personTypeFilters: string[];
  rosterContextFilters: SourceFilter[];
  tenantCompanies: string[];
}

export const DEFAULT_FILTER_STATE: HeadcountFilterState = {
  searchText: '',
  statusFilter: 'all',
  sortMode: 'lastNameAsc',
  alphaFilter: 'All',
  personTypeFilters: [],
  rosterContextFilters: [],
  tenantCompanies: [],
};

// ── Search ──

const SEARCH_FIELDS: (keyof IncidentRosterPerson)[] = [
  'displayName',
  'email',
  'phone',
  'practice',
  'floor',
  'companyName',
  'tenantName',
  'personType',
  'location',
  'assignedOfficeLocationName',
];

export const personMatchesSearch = (person: IncidentRosterPerson, query: string): boolean => {
  if (!query) return true;
  const q = query.toLowerCase();
  for (const field of SEARCH_FIELDS) {
    const val = person[field];
    if (val != null && String(val).toLowerCase().includes(q)) return true;
  }
  // Search badge fields
  const badge = person.lastIncidentDayBadgeActivity;
  if (badge) {
    if (badge.doorName?.toLowerCase().includes(q)) return true;
    if (badge.locationName?.toLowerCase().includes(q)) return true;
    if (badge.readerName?.toLowerCase().includes(q)) return true;
    if (badge.building?.toLowerCase().includes(q)) return true;
    if (badge.floor?.toLowerCase().includes(q)) return true;
    if (badge.area?.toLowerCase().includes(q)) return true;
  }
  return false;
};

// ── Filtering ──

export const applyFilters = (
  people: IncidentRosterPerson[],
  filters: HeadcountFilterState,
  getEffectiveStatus: (p: IncidentRosterPerson) => PersonStatus,
): IncidentRosterPerson[] => {
  let result = people;

  // Search
  if (filters.searchText.trim()) {
    result = result.filter((p) => personMatchesSearch(p, filters.searchText.trim()));
  }

  // Status
  if (filters.statusFilter !== 'all') {
    result = result.filter((p) => getEffectiveStatus(p) === filters.statusFilter);
  }

  // Alpha (only when search is NOT active)
  if (!filters.searchText.trim() && filters.alphaFilter !== 'All') {
    result = result.filter((p) => personMatchesAlphaRange(p, filters.alphaFilter));
  }

  // Person type (OR within group)
  if (filters.personTypeFilters.length > 0) {
    result = result.filter((p) => personMatchesPersonType(p, filters.personTypeFilters));
  }

  // Roster context (OR within group)
  if (filters.rosterContextFilters.length > 0) {
    result = result.filter((p) =>
      filters.rosterContextFilters.some((sf) => personMatchesSourceFilter(p, sf)),
    );
  }

  // Tenant company (OR within group)
  if (filters.tenantCompanies.length > 0) {
    result = applyTenantCompanyFilter(result, filters.tenantCompanies);
  }

  return result;
};

// ── Sorting ──

export const applySorting = (
  people: IncidentRosterPerson[],
  sortMode: SortMode,
  getEffectiveStatus: (p: IncidentRosterPerson) => PersonStatus,
): IncidentRosterPerson[] => {
  const lastNameCmp = (a: IncidentRosterPerson, b: IncidentRosterPerson) =>
    getLastName(a).localeCompare(getLastName(b)) || a.displayName.localeCompare(b.displayName);

  return [...people].sort((a, b) => {
    switch (sortMode) {
      case 'lastNameAsc':
        return lastNameCmp(a, b);
      case 'lastNameDesc':
        return -lastNameCmp(a, b);
      case 'statusPriority': {
        const order: Record<PersonStatus, number> = { notAccounted: 0, notChecked: 1, accounted: 2 };
        const diff = order[getEffectiveStatus(a)] - order[getEffectiveStatus(b)];
        return diff !== 0 ? diff : lastNameCmp(a, b);
      }
      case 'floor': {
        const fa = a.floor ?? '';
        const fb = b.floor ?? '';
        const diff = fa.localeCompare(fb);
        return diff !== 0 ? diff : lastNameCmp(a, b);
      }
      case 'company': {
        const ca = a.companyName ?? '';
        const cb = b.companyName ?? '';
        const diff = ca.localeCompare(cb);
        return diff !== 0 ? diff : lastNameCmp(a, b);
      }
      case 'recentlyUpdated': {
        const aTime = a.lastUpdatedAtUtc ?? '';
        const bTime = b.lastUpdatedAtUtc ?? '';
        return bTime.localeCompare(aTime);
      }
      default:
        return lastNameCmp(a, b);
    }
  });
};

// ── Active filter count (excludes search and sort) ──

export const getActiveAdvancedFilterCount = (filters: HeadcountFilterState): number => {
  let count = 0;
  // Alpha is now a first-class toolbar control, not counted in the badge
  if (filters.personTypeFilters.length > 0) count += filters.personTypeFilters.length;
  if (filters.rosterContextFilters.length > 0) count += filters.rosterContextFilters.length;
  if (filters.tenantCompanies.length > 0) count += filters.tenantCompanies.length;
  return count;
};

// ── Active filter chip labels ──

export interface ActiveFilterChip {
  key: string;
  label: string;
  removeLabel: string;
  onRemove: () => void;
}
